import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { Send } from "lucide-react";
import {
  applyLeaveRequest,
  fetchLeaveBalances,
  fetchLeaveRequests,
  fetchLeaveTypes,
} from "../../../Moduels/leave/leaveApi";
import { fetchMyCompOff } from "../../../Moduels/leave/compOffApi";
import { useEmployees } from "../../../hooks/useEmployees";
import { formatEmployeeLabel } from "../../common/employeeUtils";
import DatePickerField from "../../common/DatePickerField";
import { HrAlert, HrBtn } from "../../hr/HrDesignSystem";
import BrixigoPageLoader from "../../common/BrixigoPageLoader";
import GuidedNotice from "../../ui/GuidedNotice";
import ConfirmDialog from "../../ui/ConfirmDialog";
import { useLeaveOrg } from "./useLeaveOrg";
import { formatIndianDate } from "../../../Moduels/attendance/indianCalendarUtils";
import {
  COMP_OFF_CODE,
  CompOffOverBalanceNotice,
  LeaveRequestsTable,
  compOffExceedsBalance,
  fmtDays,
  leaveDayCount,
  pendingDaysFor,
  requestableLeaveTypes,
  useLeaveDayMarkers,
} from "./leaveFormShared";

const EMPTY_FORM = {
  employee_id: "",
  leave_type_id: "",
  start_date: "",
  end_date: "",
  duration: "full_day", // full_day | half_day
  half_day_session: "",
  reason: "",
};

const selectStyles = (invalid) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderRadius: 10,
    borderColor: invalid ? "#dc2626" : state.isFocused ? "#1a437d" : "#cbd5e1",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(26,67,125,0.14)" : "none",
    fontSize: 14,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base) => ({ ...base, fontSize: 14 }),
});

/**
 * Assign Leave (Super Admin): record leave for another employee of the organisation.
 * Uses the same POST /leave/requests contract as Apply Leave, with an explicit employee_id;
 * the server checks the caller's role and organisation and runs the normal approval workflow.
 */
export default function LvLeaveAssign() {
  const navigate = useNavigate();
  const { orgId, loading: orgLoading } = useLeaveOrg();
  const { employees, loading: employeesLoading, error: employeesError } = useEmployees();
  const year = new Date().getFullYear();

  const [types, setTypes] = useState([]);
  const [balances, setBalances] = useState([]);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [compOff, setCompOff] = useState(null);
  const [compOffLoading, setCompOffLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!orgId) return;
    fetchLeaveTypes(orgId).then((d) => setTypes(Array.isArray(d) ? d : [])).catch(() => setTypes([]));
  }, [orgId]);

  const employeeOptions = useMemo(
    () =>
      (employees || [])
        .map((e) => ({ value: String(e.employee_id), label: formatEmployeeLabel(e), employee: e }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [employees]
  );
  const selectedOption = employeeOptions.find((o) => o.value === form.employee_id) || null;
  const employee = selectedOption?.employee || null;

  const loadEmployeeLeave = () => {
    if (!orgId || !form.employee_id) {
      setBalances([]);
      setRequests([]);
      setCompOff(null);
      return;
    }
    setBalancesLoading(true);
    setCompOffLoading(true);
    fetchLeaveBalances({ organisation_id: orgId, employee_id: form.employee_id, year })
      .then((d) => setBalances(Array.isArray(d) ? d : []))
      .catch(() => setBalances([]))
      .finally(() => setBalancesLoading(false));
    fetchMyCompOff(form.employee_id)
      .then((data) => setCompOff(data))
      .catch(() => setCompOff(null))
      .finally(() => setCompOffLoading(false));
    fetchLeaveRequests({ organisation_id: orgId, employee_id: form.employee_id })
      .then((d) => setRequests(Array.isArray(d) ? d : []))
      .catch(() => setRequests([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(loadEmployeeLeave, [orgId, form.employee_id, year]);

  const applicableTypes = useMemo(
    () => (employee ? requestableLeaveTypes(types, employee) : []),
    [types, employee]
  );
  const selectedType = applicableTypes.find((t) => t.leave_type_id === form.leave_type_id) || null;
  const isCompOff = String(selectedType?.code || "").toUpperCase() === COMP_OFF_CODE;
  const allowHalfDay = selectedType?.applicability_json?.allow_half_day !== false;
  const isSingleDay = Boolean(form.start_date && form.end_date && form.start_date === form.end_date);
  const balance = balances.find((b) => b.leave_type_id === form.leave_type_id) || null;
  const compOffAvailable = Number(compOff?.summary?.available || 0);
  const available = isCompOff ? compOffAvailable : (balance ? Number(balance.available || 0) : 0);
  const pendingDays = isCompOff ? 0 : pendingDaysFor(requests, form.leave_type_id);
  const dayCount = useMemo(
    () => leaveDayCount(form.start_date, form.end_date, form.duration === "half_day"),
    [form.start_date, form.end_date, form.duration]
  );
  const compOffOverBalance = compOffExceedsBalance(isCompOff && !compOffLoading, dayCount, available);
  const dayMarkers = useLeaveDayMarkers(orgId, requests);

  // A leave type that does not apply to the newly selected employee is cleared.
  useEffect(() => {
    if (form.leave_type_id && employee && !applicableTypes.some((t) => t.leave_type_id === form.leave_type_id)) {
      setForm((f) => ({ ...f, leave_type_id: "" }));
    }
  }, [applicableTypes, employee, form.leave_type_id]);

  // Half day only for a single day on a leave type that allows it.
  useEffect(() => {
    if (form.duration === "half_day" && (!allowHalfDay || (form.start_date && form.end_date && !isSingleDay))) {
      setForm((f) => ({ ...f, duration: "full_day", half_day_session: "" }));
    }
  }, [allowHalfDay, isSingleDay, form.duration, form.start_date, form.end_date]);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setFieldErrors((fe) => (fe[k] ? { ...fe, [k]: undefined } : fe));
    setError("");
    setSuccess("");
  };

  const validate = () => {
    const errs = {};
    if (!form.employee_id) errs.employee_id = "Select an employee.";
    if (!form.leave_type_id) errs.leave_type_id = "Select a leave type.";
    if (!form.start_date) errs.start_date = "Select the From date.";
    if (!form.end_date) errs.end_date = "Select the To date.";
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      errs.end_date = "To date cannot be before From date.";
    }
    if (form.duration === "half_day") {
      if (!allowHalfDay) errs.duration = `Half day is not allowed for ${selectedType?.name || "this leave type"}.`;
      else if (!isSingleDay) errs.duration = "Half day applies only when From and To are the same date.";
      else if (!form.half_day_session) errs.half_day_session = "Select First Half or Second Half.";
    }
    if (compOffOverBalance) {
      errs.end_date = `Comp Off balance is ${fmtDays(available)}. This request is ${fmtDays(dayCount)}.`;
    }
    const maxConsecutive = Number(selectedType?.applicability_json?.max_consecutive_days) || 0;
    if (maxConsecutive > 0 && dayCount && dayCount > maxConsecutive) {
      errs.end_date = `Maximum ${fmtDays(maxConsecutive)} in a row is allowed for ${selectedType.name}.`;
    }
    return errs;
  };

  const handleSubmitClick = (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      if (!compOffOverBalance) setError("Please fix the highlighted fields.");
      return;
    }
    setShowConfirm(true);
  };

  const confirmSubmit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setShowConfirm(false);
    setSubmitting(true);
    setError("");
    setSuccess("");
    const who = selectedOption?.label || "the employee";
    try {
      const created = await applyLeaveRequest({
        organisation_id: orgId,
        employee_id: form.employee_id,
        leave_type_id: form.leave_type_id,
        start_date: form.start_date,
        end_date: form.end_date,
        reason: form.reason.trim() || null,
        half_day: form.duration === "half_day",
        half_day_session: form.duration === "half_day" ? form.half_day_session : null,
      });
      // The server decides whether the leave is approved straight away or awaits approval.
      setSuccess(
        created?.status === "approved"
          ? `Leave recorded and approved for ${who}.`
          : `Leave request created for ${who} and sent for approval.`
      );
      setForm((f) => ({ ...EMPTY_FORM, employee_id: f.employee_id, leave_type_id: f.leave_type_id }));
      setFieldErrors({});
      loadEmployeeLeave();
    } catch (err) {
      setError(err.message || "Leave could not be assigned.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/leave");
  };

  if (orgLoading) return <BrixigoPageLoader compact message="Loading…" />;

  const fieldError = (k) => (fieldErrors[k] ? <p className="text-xs text-rose-600 mt-1">{fieldErrors[k]}</p> : null);

  return (
    <div className="space-y-5">
      <form onSubmit={handleSubmitClick} className="hr-form-section p-4 sm:p-6 space-y-4" noValidate>
        <div>
          <label className="hr-form-label" htmlFor="la-employee">
            Employee <span className="text-rose-500">*</span>
          </label>
          <Select
            inputId="la-employee"
            options={employeeOptions}
            value={selectedOption}
            onChange={(opt) => {
              setField("employee_id", opt ? opt.value : "");
            }}
            isClearable
            isLoading={employeesLoading}
            placeholder="Search by employee name or code…"
            noOptionsMessage={() => (employeesLoading ? "Loading employees…" : "No eligible employees found")}
            styles={selectStyles(Boolean(fieldErrors.employee_id))}
            menuPortalTarget={document.body}
          />
          {employeesError && <p className="text-xs text-rose-600 mt-1">{employeesError}</p>}
          {!employeesLoading && !employeesError && employeeOptions.length === 0 && (
            <p className="text-xs text-slate-600 mt-1">No eligible employees found in your organisation.</p>
          )}
          {fieldError("employee_id")}
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="hr-form-label" htmlFor="la-type">
              Leave Type <span className="text-rose-500">*</span>
            </label>
            <select
              id="la-type"
              className="hr-select"
              value={form.leave_type_id}
              onChange={(e) => setField("leave_type_id", e.target.value)}
              disabled={!employee}
              aria-invalid={Boolean(fieldErrors.leave_type_id)}
            >
              <option value="">{employee ? "Select leave type" : "Select an employee first"}</option>
              {applicableTypes.map((t) => (
                <option key={t.leave_type_id} value={t.leave_type_id}>
                  {t.name} ({t.code}){t.is_paid ? "" : " — Unpaid"}
                </option>
              ))}
            </select>
            {employee && applicableTypes.length === 0 && (
              <p className="text-xs text-slate-500 mt-1">No leave types apply to this employee.</p>
            )}
            {fieldError("leave_type_id")}
          </div>

          <div>
            <span className="hr-form-label">Available Balance</span>
            <div className={`hr-input font-semibold ${compOffOverBalance ? "border-amber-400 bg-amber-50 text-amber-950" : "bg-slate-50 text-slate-800"}`}>
              {!form.employee_id || !form.leave_type_id
                ? "Select employee and leave type"
                : balancesLoading || (isCompOff && compOffLoading)
                  ? "Loading…"
                  : isCompOff
                    ? fmtDays(compOffAvailable)
                    : balance
                      ? fmtDays(available)
                      : "No balance allocated"}
            </div>
            {form.leave_type_id && pendingDays > 0 && (
              <p className="text-xs text-slate-600 mt-1">{fmtDays(pendingDays)} already in pending requests.</p>
            )}
          </div>

          <DatePickerField
            label="From Date"
            required
            value={form.start_date}
            onChange={(date) => setField("start_date", date)}
            placeholder="DD-MM-YYYY"
            max={form.end_date || undefined}
            dayMarkers={dayMarkers}
            error={fieldErrors.start_date}
          />
          <DatePickerField
            label="To Date"
            required
            value={form.end_date}
            onChange={(date) => setField("end_date", date)}
            placeholder="DD-MM-YYYY"
            min={form.start_date || undefined}
            dayMarkers={dayMarkers}
            error={fieldErrors.end_date}
          />
          {(fieldErrors.start_date || fieldErrors.end_date) && (
            <div className="sm:col-span-2 -mt-2">
              {fieldError("start_date")}
              {fieldError("end_date")}
            </div>
          )}

          <div>
            <label className="hr-form-label" htmlFor="la-duration">
              Duration <span className="text-rose-500">*</span>
            </label>
            <select
              id="la-duration"
              className="hr-select"
              value={form.duration}
              onChange={(e) => {
                setField("duration", e.target.value);
                if (e.target.value === "full_day") setField("half_day_session", "");
              }}
            >
              <option value="full_day">Full Day</option>
              {allowHalfDay && (!form.start_date || !form.end_date || isSingleDay) && (
                <option value="half_day">Half Day</option>
              )}
            </select>
            {fieldError("duration")}
            {!fieldErrors.duration && selectedType && !allowHalfDay && (
              <p className="text-xs text-slate-500 mt-1">Half day is not available for {selectedType.name}.</p>
            )}
            {!fieldErrors.duration && allowHalfDay && form.start_date && form.end_date && !isSingleDay && (
              <p className="text-xs text-slate-500 mt-1">Half day applies only when From and To are the same date.</p>
            )}
            {dayCount != null && (
              <p className={`text-xs font-semibold mt-1 ${compOffOverBalance ? "text-amber-800" : "text-slate-700"}`}>
                Total: {fmtDays(dayCount)}
                {compOffOverBalance ? ` · balance ${fmtDays(available)}` : ""}
              </p>
            )}
          </div>

          {form.duration === "half_day" && (
            <div>
              <label className="hr-form-label" htmlFor="la-session">
                Half-Day Session <span className="text-rose-500">*</span>
              </label>
              <select
                id="la-session"
                className="hr-select"
                value={form.half_day_session}
                onChange={(e) => setField("half_day_session", e.target.value)}
              >
                <option value="">Select session</option>
                <option value="first_half">First Half</option>
                <option value="second_half">Second Half</option>
              </select>
              {fieldError("half_day_session")}
            </div>
          )}
        </div>

        <div>
          <label className="hr-form-label" htmlFor="la-reason">Reason / Remarks</label>
          <textarea
            id="la-reason"
            className="hr-input min-h-[4.5rem] resize-y"
            rows={3}
            placeholder="Reason for leave or assignment remarks…"
            value={form.reason}
            onChange={(e) => setField("reason", e.target.value)}
          />
        </div>

        {compOffOverBalance ? (
          <CompOffOverBalanceNotice
            available={available}
            requested={dayCount}
            who={selectedOption?.label || "This employee"}
          />
        ) : null}

        {error && <GuidedNotice domain="leave" message={error} variant="error" onDismiss={() => setError("")} />}
        {success && <HrAlert variant="success">{success}</HrAlert>}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2 border-t border-slate-100">
          <HrBtn type="button" variant="secondary" onClick={handleCancel} disabled={submitting}>
            Cancel
          </HrBtn>
          <HrBtn type="submit" variant="primary" disabled={submitting || !orgId || compOffOverBalance}>
            <Send className="w-4 h-4" />
            {submitting ? "Assigning…" : "Assign Leave"}
          </HrBtn>
        </div>
      </form>

      {form.employee_id && (
        <section className="hr-form-section p-4 sm:p-6">
          <h3 className="text-base font-bold text-slate-800 mb-3">
            Leave Requests — {selectedOption?.label || "Employee"}
          </h3>
          <LeaveRequestsTable requests={requests} emptyText="No leave requests for this employee yet." />
        </section>
      )}

      <ConfirmDialog
        open={showConfirm}
        title="Assign leave?"
        message={
          selectedType
            ? `Assign ${selectedType.name} (${selectedType.code}) to ${selectedOption?.label || "this employee"} for ${formatIndianDate(
                form.start_date
              )} → ${formatIndianDate(form.end_date)}${dayCount ? ` (${fmtDays(dayCount)})` : ""}?`
            : "Assign this leave?"
        }
        confirmLabel="Assign"
        loading={submitting}
        onConfirm={confirmSubmit}
        onCancel={() => setShowConfirm(false)}
      />
    </div>
  );
}
